<template>
  <div class="h-full cust_card p-2 mt-2">

    <!-- GRID (HALF CARD) -->
    <div class="grid grid-cols-12 gap-4">
      <div class="col-span-12 xl:col-span-6 lg:col-span-6">

        <!-- CARD -->
        <div class="bg-white rounded cust_card p-2">

          <!-- HEADER -->
          <div class="bg-primary text-white rounded p-2 flex items-center justify-between">
            <div class="font-semibold heading-2">
              Image Quality
            </div>

            <div 
              class="text-primary heading-6 font-semibold cursor-pointer bg-white px-3 py-1 rounded uppercase"
              @click="enableEdit"
            >
              <i class="fas fa-pen-to-square"></i>
            </div>
          </div>

          <!-- BODY -->
          <div class="p-2">
            <div class="p-1">
              <p class="mb-0 flex items-center">
                <span class="text-gray4 heading-5 font-semibold" style="width:170px;">
                  Image Quality
                </span>
                <span class="dotwidth">:</span>
                <span class="text-text1 font-semibold heading-5">
                  {{ selectedQualityLabel }}
                </span>
              </p>
            </div>

            <div class="p-1">
              <p class="mb-0 flex items-center">
                <span class="text-gray4 heading-5 font-semibold" style="width:170px;">
                  Quality Value
                </span>
                <span class="dotwidth">:</span>
                <span class="text-text1 font-semibold heading-5">
                  {{ initialQuality }}
                </span>
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- POPUP (OUTSIDE CARD) -->
    <div v-if="isEditing" class="popup_overlay px-4">
      <div class="custom_dialog rounded-lg pb-4" style="width: 600px; max-height: 95%;">
        <div class="relative bg-white">

          <!-- HEADER -->
          <div class="bg-primary flex justify-between items-center p-3">
            <div class="font-medium text-white heading-3">
              Update Image Quality
            </div>

            <div>
              <span 
                class="text-text1 heading-6 font-semibold cursor-pointer bg-gray1 px-3 py-2 rounded-lg uppercase"
                @click="cancelEdit"
              >
                Cancel
              </span>

              <span 
                class="text-white heading-6 ml-2 font-semibold cursor-pointer bg-success px-3 py-2 rounded-lg uppercase"
                @click="saveQuality"
              >
                Save
              </span>
            </div>
          </div>

          <!-- BODY -->
          <div class="p-4">
            <div class="box p-2">
              <p class="label_css mb-2">Image Quality</p>

              <select
                :class="imageQualityError ? 'border border-error' : 'border border-gray-500'"
                class="outline-none text-gray-900 text-sm rounded block w-full pl-2 p-2.5 h-12 heading-4"
                v-model="tempQuality"
              >
                <option value="1.0">Original</option>
                <option value="0.8">High</option>
                <option value="0.6">Medium</option>
                <option value="0.4">Low</option>
              </select>

              <p class="text-error text-sm mt-1" v-if="imageQualityError">
                {{ imageQualityError }}
              </p>

              <p class="text-xs text-gray-500 mt-2">
                Selected: <b>{{ tempQualityLabel }}</b> 
                ({{ tempQuality }})
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script>

import ADMINAPI from '@/View/Admin/api/Admin.js'

export default {
  data () {
    return {
      isEditing: false,
      imageQualityError: '',
      sendObject: {
        imageQuality: '1.0'
      },
      initialQuality: '1.0',
      tempQuality: null,
      qualityMap: {
        '1.0': 'Original',
        '0.8': 'High',
        '0.6': 'Medium',
        '0.4': 'Low'
      }
    }
  },
  
  computed: {
    selectedQualityLabel() {        
      const key = parseFloat(this.sendObject.imageQuality || 1.0).toFixed(1)
      return this.qualityMap[key] || 'Original'
    },
    tempQualityLabel() {
      const key = parseFloat(this.tempQuality || 1.0).toFixed(1)
      return this.qualityMap[key] || 'Original'
    }
  },
  watch: {
    // Watch for changes to ensure reactivity
    'sendObject.imageQuality': {
      handler(newVal) {
        if (this.debugMode) {
          console.log('[WATCH] imageQuality changed to:', JSON.stringify(newVal), 'type:', typeof newVal)
        }
        // Clear error when value changes
        this.imageQualityError = ''
      },
      immediate: true
    }
  },
  
  mounted() {
    this.getQuality()
  },
  
  methods: {
    enableEdit() {
      this.imageQualityError = ''
      this.tempQuality = this.initialQuality
      this.isEditing = true
    },
    cancelEdit() {
      this.isEditing = false
      this.imageQualityError = ''
      this.sendObject.imageQuality = this.initialQuality
    },
    saveQuality() {
      if (!this.qualityMap[this.tempQuality]) {
        this.imageQualityError = 'Please select a valid image quality'
        return
      }
      
      console.log('=== Image Quality Save Triggered ===')
      console.log('Selected Label:', this.selectedQualityLabel)
      console.log('Numeric Value:', this.sendObject.imageQuality)
      console.log('Full Payload:', { imageQuality: this.sendObject.imageQuality })

      this.imageQualityError = ''
      this.$store?.dispatch?.('SetLoader', {status: true, message: 'Saving...'})
      ADMINAPI.UpdateImgCompSetting(
        0,
        this.tempQuality,
        response => {
            this.isEditing = false // Close modal on success
            this.getQuality()
            this.$store.dispatch('SetAlert', {showAlert: true, message: response.message, color: 'success'})
            this.$store.dispatch('SetLoader', {status: false, message: ''})
          },
          error => {
            this.$store.dispatch('SetLoader', {status: false, message: ''})
            this.$store.dispatch('SetAlert', {showAlert: true, message: error.message, color: 'error'})
          }
      )
    },
    getQuality() {
      this.$store?.dispatch?.('SetLoader', {status: true, message: 'Saving...'})
      ADMINAPI.GetImgCompSetting(
        0,
        response => {
          const raw = response?.Data?.SettingValue ?? response?.SettingValue ?? 1.0
          const normalized = parseFloat(raw).toFixed(1)

          this.sendObject.imageQuality = normalized
          this.initialQuality = normalized
          this.tempQuality = normalized
          this.$store.dispatch('SetLoader', {status: false, message: ''})
          },
          error => {
            this.$store.dispatch('SetLoader', {status: false, message: ''})
            this.$store.dispatch('SetAlert', {showAlert: true, message: error.message, color: 'error'})
          }
      )
    }
  }
}
</script>

<style scoped>

.label_css {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

.dotwidth {
  width: 15px;
}

.text-error {
  color: #dc2626;
}

.text-success {
  color: #22c55e;
}

.bg-primary {
  background-color: #3b82f6;
}

.bg-success {
  background-color: #22c55e;
}

.bg-gray1 {
  background-color: #e5e7eb;
}

.text-text1 {
  color: #1f2937;
}

.text-gray4 {
  color: #6b7280;
}

.heading-3 {
  font-size: 1.25rem;
  font-weight: 600;
}

.heading-4 {
  font-size: 1rem;
  font-weight: 500;
}

.heading-5 {
  font-size: 0.875rem;
}

.heading-6 {
  font-size: 0.75rem;
}

.popup_overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.custom_dialog {
  background: white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>